import { ExitToApp, ZoomIn, ZoomOut } from "@mui/icons-material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Box, Button, Collapse, Stack, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MiniMap, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import SzTilesBox from "../UISharedConstructors/SzTilesBox.tsx";
import { ThumbnailImage } from "./responsive-preview-imagemodal/ThumbnailImage.tsx";
import type { SzCarouselProps } from "./szCarouselTypes.ts";

const clientBaseURL = import.meta.env.VITE_API;

// Define the duration of inactivity before hiding controls
const INACTIVITY_TIMEOUT = 3000; // 3 seconds

const swipeConfidenceThreshold = 100; // Minimum distance to trigger swipe action

const variants = {
	enter: (direction: number) => ({
		x: direction > 0 ? 1000 : -1000,
		opacity: 0,
	}),
	center: {
		zIndex: 1,
		x: 0,
		opacity: 1,
	},
	exit: (direction: number) => ({
		zIndex: 0,
		x: direction < 0 ? 1000 : -1000,
		opacity: 0,
	}),
};

const SzCarousel: React.FC<SzCarouselProps> = ({ images }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [[page, direction], setPage] = useState([0, 0]);
	const [isZoomed, setIsZoomed] = useState(false);
	const [lastScale, setLastScale] = useState(0);
	const initialRender = useRef(true);
	const [showControls, setShowControls] = useState(true);
	const timeoutIdRef = useRef<number | null>(null); // Use a ref to store timeout ID
	const carouselRef = useRef<HTMLDivElement | null>(null); // Ref for carousel container

	const maxSteps = images.length;
	const imageIndex = (page + images.length) % images.length;

	const paginate = (newDirection: number) => {
		setPage([
			page === maxSteps - 1 && newDirection !== -1
				? 0
				: page === 0 && newDirection !== 1
					? maxSteps - 1
					: page + newDirection,
			newDirection,
		]);
	};

	// Handle swipe to change image when not zoomed
	const [startX, setStartX] = useState(0); // Track touch start X position
	const [isSwiping, setIsSwiping] = useState(false); // Track if user is swiping

	const handleTouchStart = (e: React.TouchEvent) => {
		if (!isZoomed && e.touches[0]) {
			setStartX(e.touches[0].clientX);
			setIsSwiping(true);
		}
	};

	const [container, setContainer] = useState<HTMLDivElement | null>(null);

	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [containerHeight, setContainerHeight] = useState<number>(0);

	const [imageNaturalWidth, setImageNaturalWidth] = useState<number>(0);
	const [imageNaturalHeight, setImageNaturalHeight] = useState<number>(0);

	const imageScale = useMemo(() => {
		if (containerWidth === 0 || containerHeight === 0 || imageNaturalWidth === 0 || imageNaturalHeight === 0) {
			return 0; // Use 0 to prevent rendering if dimensions aren't set
		}

		// Calculate aspect ratios
		const containerAspectRatio = containerWidth / containerHeight;
		const imageAspectRatio = imageNaturalWidth / imageNaturalHeight;

		// A small correction factor to avoid rounding issues and cropping
		const marginOfError = 0.001;

		let scale: number;

		// If the image is more "landscape" than the container, scale based on width
		if (imageAspectRatio > containerAspectRatio) {
			scale = containerWidth / imageNaturalWidth;
		}
		// If the image is more "portrait" than the container, scale based on height
		else {
			scale = containerHeight / imageNaturalHeight;
		}

		// Apply the margin of error to ensure no cropping
		scale = scale - marginOfError;

		// Return the scale, ensuring it's at least 0.01 to avoid negative scales
		return Math.max(scale, 0.01);
	}, [containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight]);

	const handleTouchMove = (e: React.TouchEvent) => {
		if (isSwiping && !isZoomed && e.touches[0]) {
			const touchEndX = e.touches[0].clientX;
			const swipeDistance = touchEndX - startX;

			// Detect swipe direction and trigger paginate if swipe distance exceeds threshold
			if (Math.abs(swipeDistance) > swipeConfidenceThreshold) {
				if (swipeDistance > 0) {
					paginate(-1); // Swipe right to go to the previous image
				} else {
					paginate(1); // Swipe left to go to the next image
				}
				setIsSwiping(false); // End the swipe
			}
		}
	};

	const handleTouchEnd = () => {
		setIsSwiping(false); // Reset swipe state
	};

	const handleUserInteraction = useCallback(() => {
		if (!showControls) {
			setShowControls(true);
		}
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
		}
		timeoutIdRef.current = window.setTimeout(() => {
			setShowControls(false);
		}, INACTIVITY_TIMEOUT);
	}, [showControls]);

	// Set up event listeners for user interaction within the carousel area
	useEffect(() => {
		const carouselElement = carouselRef.current;
		if (carouselElement) {
			carouselElement.addEventListener("mousemove", handleUserInteraction);
			carouselElement.addEventListener("mousedown", handleUserInteraction);

			carouselElement.addEventListener("touchstart", handleUserInteraction);
		}
		return () => {
			if (carouselElement) {
				carouselElement.removeEventListener("mousemove", handleUserInteraction);
				carouselElement.removeEventListener("mousedown", handleUserInteraction);
				carouselElement.removeEventListener("touchstart", handleUserInteraction);
			}
		};
	}, [handleUserInteraction]);

	const src = `${clientBaseURL}${images[imageIndex]?.imageUrl}` || "";
	const alt = images[imageIndex]?.description || `Obrázek ${imageIndex}`;
	const backgroundColor = "common.black";
	const zoomFactor = 10;

	const handleResize = useCallback(() => {
		if (container !== null) {
			const rect = container.getBoundingClientRect();
			setContainerWidth(rect.width);
			setContainerHeight(rect.height);
		} else {
			setContainerWidth(0);
			setContainerHeight(0);
		}
	}, [container]);

	useEffect(() => {
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [handleResize]);

	const handleImageOnLoad = (image: HTMLImageElement) => {
		setImageNaturalWidth(image.naturalWidth);
		setImageNaturalHeight(image.naturalHeight);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: All required re-render vars have been specified
	useEffect(() => {
		// Reset image natural dimensions to zero or a default value when the src changes
		setImageNaturalWidth(0);
		setImageNaturalHeight(0);

		// Load the new image and update dimensions when it's loaded
		const image = new Image();
		image.onload = () => handleImageOnLoad(image);
		image.src = src;

		// Trigger resize to ensure container dimensions are also recalculated
		handleResize();
	}, [src, handleResize]);

	// This function ensures the unzoom buttons show when ANY zoom happens
	const handleZoomChange = useCallback(
		(scale: number) => {
			// Skip initial render to avoid false positives
			if (initialRender.current) {
				initialRender.current = false;
				setLastScale(scale);
				return;
			}

			// If scale has changed since last check, consider it a zoom action
			if (scale !== lastScale) {
				const hasZoomed = scale !== imageScale;

				if (isZoomed !== hasZoomed) {
					setIsZoomed(hasZoomed);
					if (hasZoomed) {
						// Make sure controls stay visible when zoomed
						handleUserInteraction();
					}
				}

				// Update last scale to track changes
				setLastScale(scale);
			}
		},
		[imageScale, isZoomed, lastScale, handleUserInteraction],
	);

	return (
		<SzTilesBox
			height="100%"
			sx={{ justifyContent: "center", alignItems: "center", width: "100%" }}
			onClick={handleUserInteraction} // Re-show controls on click
			onTouchStart={handleTouchStart} // Start tracking swipe
			onTouchMove={handleTouchMove} // Track swipe movements
			onTouchEnd={handleTouchEnd} // End tracking swipe
		>
			<SzTilesBox.Body sx={{ backgroundColor: backgroundColor, height: "100%", width: "100%" }}>
				<div
					style={{
						width: "100%",
						height: "100%",
						backgroundColor: backgroundColor,
					}}
				>
					<Box
						ref={(el: HTMLDivElement | null) => setContainer(el)}
						sx={{
							height: isMobile ? "15rem" : "30rem",
							overflowY: "auto",
							position: "relative",
							overflow: "hidden",
						}}
					>
						<AnimatePresence initial={false} custom={direction}>
							<motion.div
								ref={carouselRef} // Reference the container to restrict the interaction
								key={page}
								custom={direction}
								variants={variants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{
									x: { type: "spring", stiffness: 300, damping: 30 },
									opacity: { duration: 0.2 },
								}}
								style={{ position: "relative", width: "100%", height: "100%" }}
							>
								{imageScale > 0 && (
									<TransformWrapper
										key={`${containerWidth}x${containerHeight}`}
										initialScale={imageScale}
										minScale={imageScale}
										maxScale={imageScale * zoomFactor}
										centerOnInit={true}
										onInit={(instance) => {
											instance.resetTransform();
											setLastScale(imageScale);
											setIsZoomed(false);
										}}
										wheel={{
											disabled: !isZoomed,
										}}
										panning={{
											disabled: !isZoomed,
										}}
										onZoom={({ state }) => {
											handleZoomChange(state.scale);
										}}
										onZoomStop={({ state }) => {
											handleZoomChange(state.scale);
										}}
									>
										{({ zoomIn, zoomOut, resetTransform }) => {
											return (
												<Box
													sx={{
														height: "100%",
														width: "100%",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
														<img alt={alt} src={src} />
													</TransformComponent>
													{/* Zoom Controls */}
													<div
														style={{
															position: "absolute",
															bottom: 10,
															right: 10,
															display: showControls ? "flex" : "none", // Hide controls if inactive
															flexDirection: isMobile ? "row" : "column",
															gap: "0.5rem",
														}}
													>
														<Collapse in={isZoomed}>
															<Stack direction={isMobile ? "row" : "column"} spacing={1}>
																<Tooltip placement={isMobile ? "top" : "left"} arrow={true} title="zrušit přiblížení">
																	<Button
																		sx={{
																			opacity: 0.8,
																		}}
																		onClick={() => {
																			resetTransform();
																			// Force update zoom state immediately
																			setIsZoomed(false);
																			setLastScale(imageScale);
																		}}
																		variant="contained"
																		color="inherit"
																	>
																		<ExitToApp />
																	</Button>
																</Tooltip>
																<Tooltip placement={isMobile ? "top" : "left"} arrow={true} title="oddálit">
																	<Button
																		onClick={() => {
																			zoomOut();
																			// Force a check after zooming out
																		}}
																		variant="contained"
																		color="secondary"
																	>
																		<ZoomOut />
																	</Button>
																</Tooltip>
															</Stack>
														</Collapse>
														<Tooltip placement={isMobile ? "top" : "left"} arrow={true} title="přiblížit">
															<Button
																onClick={(e) => {
																	e.stopPropagation();
																	e.preventDefault();
																	zoomIn();
																	// Force update zoom state immediately
																	setIsZoomed(true);
																	handleUserInteraction();
																}}
																variant="contained"
																color="primary"
															>
																<ZoomIn />
															</Button>
														</Tooltip>
													</div>

													{/* Minimap */}
													<Collapse in={isZoomed}>
														<div
															style={{
																position: "absolute",
																zIndex: 5,
																top: isMobile ? "0px" : "100px",
																left: isMobile ? "0px" : "20px",
																display: showControls ? "block" : "none", // Hide minimap if inactive
															}}
														>
															<MiniMap width={100}>
																<img
																	alt={alt}
																	src={src}
																	style={{
																		opacity: 0.8,
																	}}
																/>
															</MiniMap>
														</div>
													</Collapse>
												</Box>
											);
										}}
									</TransformWrapper>
								)}
							</motion.div>
						</AnimatePresence>

						{/* Navigation buttons */}
						{showControls && (
							<>
								<Button
									onClick={() => paginate(-1)}
									sx={{
										position: "absolute",
										top: "50%",
										left: 0,
										zIndex: 9999,
										transform: "translateY(-50%)",
										minWidth: "auto",
										padding: "2rem",
										backgroundColor: "rgba(0, 0, 0, 0.03)",
										color: "white",
										"&:hover": {
											backgroundColor: "rgba(0, 0, 0, 0.7)",
										},
									}}
								>
									<KeyboardArrowLeft />
								</Button>
								<Button
									onClick={() => paginate(1)}
									sx={{
										position: "absolute",
										top: "50%",
										right: 0,
										zIndex: 9999,
										transform: "translateY(-50%)",
										minWidth: "auto",
										padding: "2rem",
										backgroundColor: "rgba(0, 0, 0, 0.03)",
										color: "white",
										"&:hover": {
											backgroundColor: "rgba(0, 0, 0, 0.7)",
										},
									}}
								>
									<KeyboardArrowRight />
								</Button>
							</>
						)}
					</Box>
					<Box
						sx={{
							width: "calc(100%-1rem)",
							justifyContent: "safe center",
							alignItems: "safe center",
							display: "flex",
							overflowX: "auto",
							p: 1,
							backgroundColor: "grey.200",
						}}
					>
						{images.map((img, index) => (
							<ThumbnailImage
								key={img.id}
								src={`${clientBaseURL}${img?.imageUrl}` || ""}
								alt={`Obrázek ${index+1}`}
								index={index}
								onClick={() => setPage([index, 0])}
								isActive={imageIndex === index}
							/>
						))}
					</Box>
				</div>
			</SzTilesBox.Body>
		</SzTilesBox>
	);
};

export default SzCarousel;
